import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, Loader2, Mail, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/auth/AuthContext";
import { magicVerify, magicRequest } from "@/lib/api";
import { setFieldToken } from "@/lib/fieldSession";

const NEUTRAL = "If that email is registered, a link is on its way.";

// Landing page for the magic link: /field-login?token=…
// Consumes the single-use token, stores the 30-day device session, then
// drops the user on the Tech tab. Used/expired links get one neutral
// message and a "Send me a new link" form — no error codes, no hints.
export default function FieldLogin() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState("verifying"); // verifying | invalid | done
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  // Single-use token: StrictMode double-invokes effects in dev, so consume at most once per mount.
  const consumed = useRef(false);

  useEffect(() => {
    const token = new URLSearchParams(location.search).get("token");
    window.history.replaceState({}, "", "/field-login");
    if (!token) { if (!consumed.current) setState("invalid"); return; }
    if (consumed.current) return;
    consumed.current = true;
    (async () => {
      try {
        const device = `${navigator.platform || "device"} · ${/iPhone|iPad/.test(navigator.userAgent) ? "iOS" : /Android/.test(navigator.userAgent) ? "Android" : "browser"}`;
        const { data } = await magicVerify(token, device);
        await setFieldToken(data.session_token);
        await refresh();
        setState("done");
        navigate(data.user?.landing_page || "/field", { replace: true });
      } catch {
        setState("invalid");
      }
    })();
  }, [location.search, navigate, refresh]);

  const resend = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await magicRequest(email.trim().toLowerCase()); } catch { /* neutral */ }
    setSent(true); setBusy(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-slate-200 p-8 space-y-5" data-testid="field-login-card">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-slate-900 text-white rounded-lg flex items-center justify-center mb-3"><Lock className="w-5 h-5" /></div>
          <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>ServiceOps</h1>
          <p className="text-xs text-slate-500 mt-0.5">Field sign-in</p>
        </div>
        {state === "verifying" && (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-600" data-testid="field-login-verifying"><Loader2 className="w-4 h-4 animate-spin" /> Signing you in…</div>
        )}
        {state === "invalid" && (
          <>
            <div className="text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2" data-testid="field-login-invalid">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>This sign-in link is no longer valid. Links work once and expire after 15 minutes.</span>
            </div>
            {sent ? (
              <p className="text-sm text-slate-700 text-center" data-testid="field-login-sent">{NEUTRAL}</p>
            ) : (
              <form onSubmit={resend} className="space-y-2">
                <Input type="email" required placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="field-login-email" />
                <Button type="submit" disabled={busy || !email} className="w-full h-10 bg-slate-900 hover:bg-slate-800" data-testid="field-login-resend">
                  <Mail className="w-4 h-4 mr-2" /> Send me a new link
                </Button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
